/**
 * Calendar time zones (owner decision, Oct 2026: the calendar shows each
 * viewer's own computer clock, so an adviser in Sydney sees what Outlook shows).
 *
 * Three kinds of time move through the calendar code:
 *  - Instants: a Date, or an ISO string ending in Z or an offset. Graph events
 *    arrive like this (/api/calendar/events appends Z).
 *  - Brisbane wall time with NO offset: what the calendar and booking endpoints
 *    take and what the client record stores (assignment.welcomeCallDateTime,
 *    arBookedDateTime, task.booking.*). The server sends it to Graph labelled
 *    Australia/Brisbane. A string with no offset only ever means Brisbane.
 *  - Grid times: the grid, forms and drag maths work on the viewer's clock and
 *    always write the viewer's offset, e.g. 2026-10-07T17:00:00+11:00.
 *
 * Every write converts with toBrisbaneWall() just before the request.
 */

export const BRISBANE_TZ = 'Australia/Brisbane';
const BRISBANE_OFFSET = '+10:00';

const pad = (n) => String(n).padStart(2, '0');

let viewTimeZoneOverride = null;

export function isValidTimeZone(timeZone) {
  if (!timeZone || typeof timeZone !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** The viewer's own zone (their computer's), falling back to Brisbane. */
export function getViewTimeZone() {
  if (viewTimeZoneOverride) return viewTimeZoneOverride;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (isValidTimeZone(tz)) return tz;
  } catch {
    /* very old browsers: fall through */
  }
  return BRISBANE_TZ;
}

/** Tests only: pin the viewer's zone (null restores the computer's). */
export function setViewTimeZoneForTests(timeZone) {
  viewTimeZoneOverride = timeZone || null;
}

/**
 * Reads any calendar time string as an instant. Strings with Z or an offset
 * are exact; strings with no offset are Brisbane wall time.
 */
export function parseCalendarTime(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const trimmed = String(value).trim().replace(/(\d{2}:\d{2}:\d{2})\.\d+/, '$1');
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const date = new Date(hasZone ? trimmed : `${trimmed}${BRISBANE_OFFSET}`);
  return Number.isNaN(date.getTime()) ? null : date;
}

const partsFormatters = new Map();
function partsFormatter(timeZone) {
  let f = partsFormatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    partsFormatters.set(timeZone, f);
  }
  return f;
}

const WEEKDAY_INDEX = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

/** Wall-clock parts of an instant in a zone; weekdayIndex is Mon=0 … Sun=6. */
export function zonedParts(value, timeZone = getViewTimeZone()) {
  const date = parseCalendarTime(value);
  if (!date) return null;
  const out = {};
  partsFormatter(timeZone).formatToParts(date).forEach((p) => { out[p.type] = p.value; });
  return {
    ymd: `${out.year}-${out.month}-${out.day}`,
    hour: Number(out.hour) % 24,
    minute: Number(out.minute),
    second: Number(out.second),
    weekdayIndex: WEEKDAY_INDEX[out.weekday],
  };
}

/** YYYY-MM-DD of an instant in a zone. */
export function zonedYmd(value = new Date(), timeZone = getViewTimeZone()) {
  return zonedParts(value, timeZone)?.ymd || '';
}

export function zonedHoursMinutes(value, timeZone = getViewTimeZone()) {
  const p = zonedParts(value, timeZone);
  return p ? { hour: p.hour, minute: p.minute } : { hour: 0, minute: 0 };
}

/** Minutes the zone is ahead of UTC at that instant (Sydney in October: 660). */
export function zoneOffsetMinutes(value, timeZone = getViewTimeZone()) {
  const date = parseCalendarTime(value);
  const p = zonedParts(date, timeZone);
  if (!p) return 0;
  const [y, m, d] = p.ymd.split('-').map(Number);
  const wallAsUtc = Date.UTC(y, m - 1, d, p.hour, p.minute, p.second);
  const wholeSeconds = Math.floor(date.getTime() / 1000) * 1000;
  return Math.round((wallAsUtc - wholeSeconds) / 60000);
}

/** The instant a zone's clock reads `totalMinutes` past midnight on `ymd` (minutes may pass 1440). */
export function zonedWallToDate(ymd, totalMinutes, timeZone = getViewTimeZone()) {
  const [y, m, d] = String(ymd).split('-').map(Number);
  const wallAsUtc = Date.UTC(y, m - 1, d, 0, totalMinutes);
  // Two passes so a day that changes offset (daylight saving) lands on the right side.
  const guess = wallAsUtc - zoneOffsetMinutes(new Date(wallAsUtc), timeZone) * 60000;
  return new Date(wallAsUtc - zoneOffsetMinutes(new Date(guess), timeZone) * 60000);
}

function formatOffset(minutes) {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/** An instant written in a zone with its offset, e.g. 2026-10-07T17:00:00+11:00. */
export function formatInstantInZone(value, timeZone = getViewTimeZone()) {
  const date = parseCalendarTime(value);
  const p = zonedParts(date, timeZone);
  if (!p) return null;
  return `${p.ymd}T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}${formatOffset(zoneOffsetMinutes(date, timeZone))}`;
}

/** A grid time: the zone's wall clock on `ymd` plus `totalMinutes`, with the zone's offset. */
export function formatZonedWall(ymd, totalMinutes, timeZone = getViewTimeZone()) {
  return formatInstantInZone(zonedWallToDate(ymd, totalMinutes, timeZone), timeZone);
}

/** Brisbane wall time with no offset — the only form calendar and booking endpoints take. */
export function toBrisbaneWall(value) {
  const p = zonedParts(value, BRISBANE_TZ);
  if (!p) return null;
  return `${p.ymd}T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`;
}

/** Graph fetch window covering whole days on the zone's clock, as UTC instants. */
export function utcRangeForDays(startYmd, endYmd, timeZone = getViewTimeZone()) {
  const toUtc = (date) => `${date.toISOString().slice(0, 19)}Z`;
  const end = zonedWallToDate(endYmd, 24 * 60, timeZone);
  return {
    startDateTime: toUtc(zonedWallToDate(startYmd, 0, timeZone)),
    endDateTime: toUtc(new Date(end.getTime() - 1000)),
  };
}

const TIME_OPTS = { hour: 'numeric', minute: '2-digit', hour12: true };

/** e.g. "5:00 pm" on the zone's clock. */
export function formatTimeInZone(value, timeZone = getViewTimeZone()) {
  const date = parseCalendarTime(value);
  return date ? date.toLocaleTimeString('en-AU', { ...TIME_OPTS, timeZone }) : '';
}

/** e.g. "5:00 pm – 5:30 pm" on the zone's clock. */
export function formatTimeRangeInZone(start, end, timeZone = getViewTimeZone()) {
  const a = formatTimeInZone(start, timeZone);
  const b = formatTimeInZone(end, timeZone);
  if (!a) return '';
  return b ? `${a} – ${b}` : a;
}

/** Date label on the zone's clock, e.g. { weekday: 'short', day: 'numeric', month: 'short' } → "Wed 7 Oct". */
export function formatDateInZone(value, options, timeZone = getViewTimeZone()) {
  const date = parseCalendarTime(value);
  return date ? date.toLocaleDateString('en-AU', { ...options, timeZone }) : '';
}

/** "Sydney" from "Australia/Sydney". */
export function timeZoneCity(timeZone) {
  return String(timeZone || '').split('/').pop().replace(/_/g, ' ');
}

/** "AEDT", "AEST", "ACDT"… ("GMT+1" style outside Australia). */
export function timeZoneAbbreviation(timeZone, value = new Date()) {
  try {
    const date = parseCalendarTime(value) || new Date();
    const part = new Intl.DateTimeFormat('en-AU', { timeZone, timeZoneName: 'short' })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName');
    return part?.value || '';
  } catch {
    return '';
  }
}

/** "Sydney time (AEDT)" — the caption calendars show for the viewer's clock. */
export function describeTimeZone(timeZone = getViewTimeZone(), value = new Date()) {
  const abbr = timeZoneAbbreviation(timeZone, value);
  if (!timeZone || timeZone === 'UTC' || !timeZone.includes('/')) return abbr || 'UTC';
  const city = timeZoneCity(timeZone);
  return abbr ? `${city} time (${abbr})` : `${city} time`;
}

/**
 * The booked time on the adviser's own clock, for when someone books into
 * another person's calendar: "5:00 pm – 5:30 pm for Tharindu (Sydney time)".
 * Empty when their clock reads the same as the viewer's, or their zone is unknown.
 */
export function formatAdviserLocalTime({
  start,
  end,
  adviserTimeZone,
  adviserName = '',
  viewTimeZone = getViewTimeZone(),
}) {
  if (!isValidTimeZone(adviserTimeZone)) return '';
  const s = parseCalendarTime(start);
  if (!s) return '';
  if (zoneOffsetMinutes(s, adviserTimeZone) === zoneOffsetMinutes(s, viewTimeZone)) return '';
  const times = formatTimeRangeInZone(s, end, adviserTimeZone);
  const day = zonedYmd(s, adviserTimeZone) !== zonedYmd(s, viewTimeZone)
    ? `${formatDateInZone(s, { weekday: 'short' }, adviserTimeZone)} `
    : '';
  const who = adviserName ? ` for ${adviserName}` : '';
  return `${day}${times}${who} (${timeZoneCity(adviserTimeZone)} time)`;
}
